import { useMemo } from "react";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { buildSignalCategoryTheme } from "./signalCategories";

/**
 * Signal → category → colour, from one source, so a signal's chip is the same
 * colour on every surface (topic cards, the drilldown table, Highlights, the
 * speaker detail, …). A signal is coloured by its category:
 *
 *   - each published signal tag's category comes from one lean, cached `tags`
 *     query — the same one the Topics sidebar uses (title + category.value),
 *     keyed by the tag's `_id`, which is exactly the id an annotation's signal
 *     tag carries;
 *   - the category → colour palette is the tenant's own, via
 *     buildSignalCategoryTheme(platformConfigs.SIGNAL_CATEGORIES) — the same
 *     palette the Topics theme is built from.
 *
 * Returns `colorOfSignal(signalId)` (null when the signal has no category, so
 * callers can fall back to a neutral tone), `categoryOfSignal(signalId)`,
 * `isSignal(tagId)` (whether a tag id is one of the published signals — the
 * tags collection also holds persona tags), and the `theme` for anything
 * that needs the category palette directly.
 */
export const useSignalColors = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const signalsSubThemeTagId =
    platformConfigs?.ai?.aiAnnotation?.dualAgentConfig?.signalSubThemeTagId || "signals";

  const { data: signalsData } = useGetDataQuery({
    contentTypes: ["tags"],
    findQuery: { kp_published_status: "published", "tags.subThemes.data.tagId": signalsSubThemeTagId },
    projection: { "main.title": 1, "main.category.value": 1 },
  });

  const theme = useMemo(
    () => buildSignalCategoryTheme(platformConfigs?.SIGNAL_CATEGORIES),
    [platformConfigs]
  );

  const categoryById = useMemo(
    () =>
      Object.fromEntries(
        (signalsData?.data || []).map((t) => [String(t._id), t.main?.category?.value || null])
      ),
    [signalsData]
  );

  return useMemo(() => {
    const isSignal = (tagId) => String(tagId) in categoryById;
    const categoryOfSignal = (signalId) => categoryById[String(signalId)] || null;
    const colorOfSignal = (signalId) => {
      const cat = categoryOfSignal(signalId);
      return cat ? theme.fillOf(cat) : null;
    };
    return { theme, isSignal, categoryOfSignal, colorOfSignal };
  }, [categoryById, theme]);
};
